// @ts-nocheck

import _ from 'lodash';

import { CommonActionTypes } from '../../../types/actions/common';
import { UserActionTypes } from '../../../types/actions/user';
import { type BetslipStatus } from '../../../types/betslip';
import { KLRoundIndexByRoundId } from '../constants/order';
import { KLStateActionTypes } from '../types/actions/kl';
import { type KLCollectibleId, type KLCollectibleStatus } from '../types/collectible';
import { type KLGlobalStatus } from '../types/global';
import { type KLNotificationType } from '../types/notification';
import { type KLRound } from '../types/round';
import { KLUserStatuses, type KLUserStatus } from '../types/user';

export type KLUserBadgeState = { status: KLCollectibleStatus; betslipReference?: string };
export type KLUserBadgesState = { [id: KLCollectibleId]: KLUserBadgeState };
export type KLUserOrientationCollectibleState = { status: KLCollectibleStatus; betslipReference?: string };
export type KLUserOrientationCollectiblesState = { [id: KLCollectibleId]: KLUserOrientationCollectibleState };
export type KLUserPoleBetState = {
  status: KLCollectibleStatus;
  betslipReference: string;
  time: number;
  notified: boolean;
};
export type KLUserPoleState = {
  totalTime: number;
  maxAttempts: number;
  bets: Array<KLUserPoleBetState>;
};
export type KLUserNotificationState = {
  type: KLNotificationType;
  data: {
    badgeId?: KLCollectibleId;
    roundId?: KLRound;
    betslipReference?: string;
  };
};
export type KLUserBetslipState = {
  betslipReference: string;
  betslipStatus: BetslipStatus;
  validationDate: number;
  betAmount: number;
  matchIds: Array<number>;
  odds: number;
};
export type KLUserBetslipsState = {
  [betslipReference: string]: KLUserBetslipState;
};

export type KLUserState = {
  status: KLUserStatus;
  round: KLRound;
  collars: number;
  votes: number;
  pendingCollars: number;
  lastMoveTs: number | null;
  badges: KLUserBadgesState;
  remainingKnifes: number;
  orientation: Array<KLUserOrientationCollectiblesState>;
  pole: KLUserPoleState;
  notifications: Array<KLUserNotificationState>;
  betslips: KLUserBetslipsState;
};

export type KLMiscState = {
  tmpNotifications?: Array<{
    type: KLNotificationType;
    data: {
      badgeId?: KLCollectibleId;
      roundId?: KLRound;
      betslipReference?: string;
    };
  }> | null;
  orientationPaginationIndex?: number;
  homeVisitedIndex?: number | null;
  homeNavigationIsAllowed: boolean;
};

export type KLGlobalState = {
  status?: KLGlobalStatus | null;
  version?: string | null;
  criteria?: unknown;
  race?: string;
  operationEndDate?: number;
};

export type KLSpecificRule = {
  stakeMultiplicator?: number;
  h1StakeMultiplicator?: number;
  h2StakeMultiplicator?: number;
  h4StakeMultiplicator?: number;
  h6StakeMultiplicator?: number;
  minOdds?: {
    [key: KLCollectibleId]: number;
  };
};

export type KLRulesState = {
  specifics: {
    [key: KLRound]: KLSpecificRule;
  };
};

export type KLState = {
  user: KLUserState;
  global: KLGlobalState;
  misc: KLMiscState;
  rules: KLRulesState;
};

export const klInitialState: KLState = {
  misc: {
    homeVisitedIndex: null,
    homeNavigationIsAllowed: true,
    tmpNotifications: null,
  },
  global: {},
  user: {},
  rules: {
    global: {},
    specifics: {},
  },
};

const actionTypesToReactOn = [
  KLStateActionTypes.UPDATE_KL_STATE,
  KLStateActionTypes.SKIP_CURRENT_KL_TMP_NOTIFICATION,
  KLStateActionTypes.SKIP_ALL_KL_TMP_NOTIFICATIONS,
  KLStateActionTypes.FILL_TEMPORARY_KL_NOTIFICATIONS,
  KLStateActionTypes.SET_KL_ORIENTATION_PAGINATION_INDEX,
  KLStateActionTypes.SET_KL_HOME_VISITED_INDEX,
  KLStateActionTypes.REVIEW_KL_ADVENTURE,
  CommonActionTypes.RECEIVE_ENTITIES,
  UserActionTypes.SET_DISCONNECTED,
];

export default (state: KLState = klInitialState, action: unknown) => {
  const { type, payload } = action;
  const { user, global, misc, rules } = state;

  if (actionTypesToReactOn.indexOf(type) !== -1) {
    if (type === KLStateActionTypes.UPDATE_KL_STATE) {
      const { userStateMutation } = payload;

      const nextUserState = {};
      _.mergeWith(nextUserState, user, userStateMutation, (objValue, srcValue) => {
        if (_.isArray(srcValue)) {
          return srcValue;
        }
      });

      return {
        ...state,
        user: nextUserState,
        misc: {
          ...misc,
          orientationPaginationIndex: (nextUserState?.orientation?.length ?? 1) - 1,
          homeNavigationIsAllowed: !(nextUserState.status === KLUserStatuses.OUT && misc.homeVisitedIndex === null),
        },
      };
    }

    if (type === KLStateActionTypes.SKIP_CURRENT_KL_TMP_NOTIFICATION) {
      const { tmpNotifications } = misc;
      if (tmpNotifications) {
        const [, ...next]: Array<unknown> = tmpNotifications;
        const copy = JSON.parse(JSON.stringify(next));

        return {
          ...state,
          misc: {
            ...misc,
            tmpNotifications: copy,
          },
        };
      }
    }

    if (type === KLStateActionTypes.SKIP_ALL_KL_TMP_NOTIFICATIONS) {
      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: [],
        },
      };
    }

    if (type === KLStateActionTypes.FLUSH_KL_NOTIFICATIONS) {
      return {
        ...state,
        user: {
          ...user,
          notifications: [],
        },
      };
    }

    if (type === KLStateActionTypes.FILL_TEMPORARY_KL_NOTIFICATIONS) {
      const { notifications = [] } = user ?? {};
      const copy = JSON.parse(JSON.stringify(notifications));

      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: copy,
        },
      };
    }

    if (type === KLStateActionTypes.SET_KL_ORIENTATION_PAGINATION_INDEX) {
      const { index } = payload;

      return {
        ...state,
        misc: {
          ...misc,
          orientationPaginationIndex: index,
        },
      };
    }

    if (type === UserActionTypes.SET_DISCONNECTED) {
      return {
        ...klInitialState,
        global,
      };
    }

    if (type === KLStateActionTypes.SET_KL_HOME_VISITED_INDEX) {
      const { index } = payload;

      return {
        ...state,
        misc: {
          ...misc,
          homeVisitedIndex: index,
        },
      };
    }

    if (type === KLStateActionTypes.REVIEW_KL_ADVENTURE) {
      const { round } = user;

      return {
        ...state,
        misc: {
          ...misc,
          homeVisitedIndex: KLRoundIndexByRoundId[round] ?? 0,
          homeNavigationIsAllowed: true,
        },
      };
    }

    if (type === CommonActionTypes.RECEIVE_ENTITIES) {
      const { settings } = payload;
      const { kohlanta } = settings ?? {};
      const { version, status, criteria, race, operationEndDate, rules: incomingRules } = kohlanta ?? {};

      return {
        ...state,
        global: {
          ...global,
          status: (status === null || kohlanta === null) && global.status ? undefined : status ?? global.status,
          version: (version === null || kohlanta === null) && global.version ? undefined : version ?? global.version,
          criteria:
            (criteria === null || kohlanta === null) && global.criteria ? undefined : criteria ?? global.criteria,
          race: (race === null || kohlanta === null) && global.race ? undefined : race ?? global.race,
          operationEndDate:
            (operationEndDate === null || kohlanta === null) && global.operationEndDate
              ? undefined
              : operationEndDate ?? global.operationEndDate,
        },
        rules: (incomingRules === null || kohlanta === null) && rules ? undefined : incomingRules ?? rules,
      };
    }
  }

  return state;
};
